import { ResourceClient, type HttpClient } from "@dl/web-core-js/api";
import type { ProductionOrderDTO, ProductionOrderStatus } from "./production";
import type { ServiceOrderLineDTO } from "./servicedesk";

/**
 * The shop floor: a lot's components and their steps, what was reported at
 * each step, the board, scans and the trucks to Terrebonne.
 *
 * Steps and components are written by the lot's release; quantities and status
 * move only through step reports (append-only events). Wire names match the
 * Java `@JsonProperty`s.
 */

export type ProcessCountingUnit = "PART" | "CUTBOARD";
export type ProductionOperationStatus = "PENDING" | "ACTIVE" | "DONE" | "SKIPPED";
export type ProductionEventKind = "START" | "REPORT" | "SEND" | "MISSING" | "FINISH" | "SKIP" | "REOPEN";
export type ProductionTransferStatus = "SHIPPED" | "RECEIVED" | "CANCELLED";

/** A manufactured part a lot makes, with where it stands. Its scan code is `<lot>-<sequence>`. */
export interface ProductionOrderComponentDTO {
  id: number;
  production_order_id: number;
  production_order_number: number;
  sequence: number;
  /** `<lot>-<sequence>`: what the floor scans (a traveller adds `.<pallet>`). */
  code: string;
  part_id: number;
  part_number?: string | null;
  part_name?: string | null;
  letter: string | null;
  /** Parts to make. */
  quantity: number;
  parts_per_cutboard: number | null;
  /** Cutboards to cut and edge: ceil(quantity / parts_per_cutboard). */
  cutboard_quantity?: number | null;
  pallet_quantity: number | null;
  /** Travellers printed: one per pallet. */
  pallet_count?: number | null;
  bom_id: number | null;
  part_file_id: number | null;
  /** The part has a newer current drawing than the one this lot was released with. */
  has_newer_drawing?: boolean;
  /** Imported runs: SIGM's child project number, which scans too. */
  sigm_project_number: number | null;
  note: string | null;
  /** Good parts through its last step: ready to ship. */
  quantity_done?: number | null;
  quantity_missing?: number | null;
  /** 0 to 1. */
  progress?: number | null;
  step_count?: number | null;
  step_done_count?: number | null;
}

/** An order's step: one process for one component, or for the whole order (an assembly order's kit, foam, cornières, packing) when the component is null. */
export interface ProductionOrderOperationDTO {
  id: number;
  production_order_id: number;
  production_order_number: number;
  production_order_status?: ProductionOrderStatus | null;
  production_order_component_id: number | null;
  component_code?: string | null;
  component_sequence?: number | null;
  part_id?: number | null;
  part_number?: string | null;
  part_name?: string | null;
  letter?: string | null;
  position: number;
  process_id: number;
  process_code?: string | null;
  /** French name, as the floor says it. */
  process_name?: string | null;
  /** CUTBOARD: Cut and Edge of a nested part count boards, not parts. */
  counting_unit?: ProcessCountingUnit | null;
  program_code: string | null;
  /** Parts. */
  quantity_required: number;
  /** In the step's own unit (cutboards when nested). */
  quantity_required_counted?: number | null;
  /** Good, in the step's own unit. */
  quantity_done?: number | null;
  quantity_scrap?: number | null;
  quantity_missing?: number | null;
  /**
   * Parts this step may have done in total: what the steps it waits for have
   * passed (per their FLOW or FINISH rule), or the component's quantity when it
   * waits for nothing. Past it, a report needs an override reason.
   */
  quantity_available?: number | null;
  /** An external step (Paint): parts sent out. quantity_done is what came back. */
  quantity_sent?: number | null;
  /** Done outside the plant: reported as sent, then back. */
  is_external?: boolean;
  /** The steps this one waits for. */
  depends_on_ids?: number[] | null;
  /** Something was reported past what the step allowed (see its history). */
  has_override?: boolean;
  status: ProductionOperationStatus;
  started_at: string | null;
  finished_at: string | null;
  last_event_at?: string | null;
  note: string | null;
  /** Taken from the part's fields because its revision has no routing. */
  is_derived: boolean;
  /** Machines allowed; empty: any of the process. */
  station_ids: number[];
  station_codes?: string | null;
  /** Started, not done, nothing reported for longer than the shop-floor setting. */
  is_stalled?: boolean;
  /** An assembly order's packing step: the box it packs. */
  box_number?: number | null;
}

export interface ProductionOperationEventDTO {
  id: number;
  production_order_operation_id: number;
  production_order_id: number;
  production_order_number: number;
  component_code?: string | null;
  part_number?: string | null;
  process_code?: string | null;
  process_name?: string | null;
  kind: ProductionEventKind;
  quantity_good: number;
  quantity_scrap: number;
  production_scrap_reason_id: number | null;
  scrap_reason_name?: string | null;
  quantity_missing: number | null;
  station_id: number | null;
  station_code?: string | null;
  operator_id: number | null;
  operator_name?: string | null;
  pallet_number: number | null;
  note: string | null;
  recorded_at: string;
  created_by_account_id: number | null;
  /** SEND: parts sent out. */
  quantity_sent?: number | null;
  /** Reported past what the step allowed, on purpose. */
  is_override?: boolean;
  override_reason?: string | null;
  /** By how much, in the step's unit. */
  quantity_over?: number | null;
}

/** Something reported at a step. */
export interface StepReportRequest {
  kind: ProductionEventKind;
  quantity_good?: number | null;
  quantity_scrap?: number | null;
  production_scrap_reason_id?: number | null;
  /** MISSING: how many are missing now; 0 clears. */
  quantity_missing?: number | null;
  station_id?: number | null;
  operator_badge?: string | null;
  pallet_number?: number | null;
  note?: string | null;
  /** SEND, on an external step (Paint): parts sent out; negative corrects. */
  quantity_sent?: number | null;
  /**
   * Going past what the step's dependencies allow: why. The answer to a 409
   * with code OVERRIDE; at a kiosk the badge must be allowed to override.
   */
  override_reason?: string | null;
  /** Materials counted at this step (panels used at the saw); consumption follows the counts from then on. */
  materials_used?: MaterialUse[] | null;
  /** Why the materials counted with it go past their estimate. */
  materials_reason?: string | null;
}

/** How much of one of a lot's materials was used, counted at a step; negative corrects a count. */
export interface MaterialUse {
  production_order_material_id: number;
  quantity: number;
}

/** A material consumed at a lot's step, as the station counts it. */
export interface StepMaterialDTO {
  production_order_material_id: number;
  part_number: string;
  part_name: string | null;
  unit_code: string | null;
  /** The estimate for the line (the bill of materials). */
  quantity_required: number;
  /** Counted so far at the station; null when never counted. */
  quantity_counted: number | null;
  /** What the ledger has taken for the line so far. */
  quantity_consumed: number;
  is_consumed_whole: boolean;
  /** What one unit of the lot needs. */
  quantity_per_unit: number;
  /** A piece made at the step out of other lines (a pad cut from a sheet): not taken from stock. */
  is_made: boolean;
  /** Raw stock this step counts by hand (panels, foam sheets): consumption follows the count. */
  is_countable: boolean;
}

/** Everything a floor board draws, in one answer. */
export interface ShopFloorBoardDTO {
  lots: ProductionOrderDTO[];
  components: ProductionOrderComponentDTO[];
  operations: ProductionOrderOperationDTO[];
  refresh_seconds: number;
  stalled_after_hours: number;
  as_of: string;
}

/** What a scanned code is. */
export interface ScanResultDTO {
  code: string;
  lot: ProductionOrderDTO;
  /** Null when a lot label was scanned. */
  component: ProductionOrderComponentDTO | null;
  pallet_number: number | null;
  operations: ProductionOrderOperationDTO[];
  /** The step the scanning station's process does, if any. */
  station_operation_id: number | null;
}

export interface OperatorBadgeDTO {
  operator_id: number;
  name: string;
  badge_code: string;
  /** This badge may go past what a step allows, with a reason. */
  has_override_right?: boolean;
}

export interface ProductionTransferLineDTO {
  id: number;
  production_transfer_id: number;
  production_order_component_id: number;
  component_code?: string | null;
  part_number?: string | null;
  part_name?: string | null;
  letter?: string | null;
  quantity_shipped: number;
  /** Null until received. */
  quantity_received: number | null;
  note: string | null;
}

/** A truckload of a lot's parts, Laval to Terrebonne. */
export interface ProductionTransferDTO {
  id: number;
  number: number;
  production_order_id: number;
  production_order_number?: number;
  from_site_id: number;
  from_site_code?: string | null;
  to_site_id: number;
  to_site_code?: string | null;
  status: ProductionTransferStatus;
  shipped_date: string | null;
  truck_reference: string | null;
  note: string | null;
  shipped_by_account_id?: number | null;
  received_at?: string | null;
  received_by_account_id?: number | null;
  lines: ProductionTransferLineDTO[];
}

/** A 409 the operator may override: resend with the confirmation flag. */
export function needsConfirmation(failure: unknown): string | null {
  const body = (failure as { body?: unknown } | null)?.body;
  if (body && typeof body === "object" && "code" in body && (body as { code?: unknown }).code === "CONFIRM") {
    const message = (body as { message?: unknown }).message;
    return typeof message === "string" ? message : "";
  }
  return null;
}

/** A 409 past what the step's dependencies allow: resend with an override reason. */
export interface OverrideNeeded {
  message: string;
  /** What the step may have done in total, in its own unit. */
  allowed: number | null;
}

export function needsOverride(failure: unknown): OverrideNeeded | null {
  const body = (failure as { body?: unknown } | null)?.body;
  if (body && typeof body === "object" && "code" in body && (body as { code?: unknown }).code === "OVERRIDE") {
    const { message, allowed } = body as { message?: unknown; allowed?: unknown };
    return { message: typeof message === "string" ? message : "", allowed: typeof allowed === "number" ? allowed : allowed == null ? null : Number(allowed) };
  }
  return null;
}

export class ProductionOrderComponentClient extends ResourceClient<ProductionOrderComponentDTO> {
  constructor(http: HttpClient) {
    super(http, "/production/production-order-component");
  }

  forLot(lotId: number): Promise<ProductionOrderComponentDTO[]> {
    return this.find({ page: 0, size: 1000, filters: { production_order_id: [String(lotId)] }, sort: ["sequence,asc"] }).then((page) => page.content);
  }
}

export class ProductionOrderOperationClient extends ResourceClient<ProductionOrderOperationDTO> {
  constructor(http: HttpClient) {
    super(http, "/production/production-order-operation");
  }

  forLot(lotId: number): Promise<ProductionOrderOperationDTO[]> {
    return this.find({ page: 0, size: 5000, filters: { production_order_id: [String(lotId)] } }).then((page) => page.content);
  }
}

export class OperationEventClient extends ResourceClient<ProductionOperationEventDTO> {
  constructor(http: HttpClient) {
    super(http, "/production/operation-event");
  }

  /** Newest first. */
  forLot(lotId: number, size = 500): Promise<ProductionOperationEventDTO[]> {
    return this.find({ page: 0, size, filters: { production_order_id: [String(lotId)] }, sort: ["recorded_at,desc"] }).then((page) => page.content);
  }

  forStation(stationId: number, size = 10): Promise<ProductionOperationEventDTO[]> {
    return this.find({ page: 0, size, filters: { station_id: [String(stationId)] }, sort: ["recorded_at,desc"] }).then((page) => page.content);
  }
}

export class ShopFloorClient {
  constructor(private readonly http: HttpClient) {}

  board(statuses?: ProductionOrderStatus[]): Promise<ShopFloorBoardDTO> {
    return this.http.get<ShopFloorBoardDTO>("/production/shop-floor/board", { query: { status: statuses } });
  }

  queue(stationId: number): Promise<ProductionOrderOperationDTO[]> {
    return this.http.get<ProductionOrderOperationDTO[]>(`/production/shop-floor/station/${String(stationId)}/queue`);
  }

  scan(code: string, stationId?: number | null): Promise<ScanResultDTO> {
    return this.http.get<ScanResultDTO>("/production/shop-floor/scan", { query: { code, station_id: stationId ?? undefined } });
  }

  badge(code: string): Promise<OperatorBadgeDTO> {
    return this.http.get<OperatorBadgeDTO>("/production/shop-floor/badge", { query: { code } });
  }

  /** One event on a step; answers the step as it now stands. 409 code OVERRIDE: see {@link needsOverride}. */
  report(operationId: number, request: StepReportRequest): Promise<ProductionOrderOperationDTO> {
    return this.http.post<ProductionOrderOperationDTO>(`/production/shop-floor/operation/${String(operationId)}/report`, request);
  }

  /** The materials consumed at a step, to count them there (panels used at the saw). */
  materials(operationId: number): Promise<StepMaterialDTO[]> {
    return this.http.get<StepMaterialDTO[]>(`/production/shop-floor/operation/${String(operationId)}/materials`);
  }

  /** Materials counted without reporting parts (a later count, or a correction); stock follows at once. */
  count(
    operationId: number,
    request: { materials_used: MaterialUse[]; station_id?: number | null; operator_badge?: string | null; reason?: string | null },
  ): Promise<StepMaterialDTO[]> {
    return this.http.post<StepMaterialDTO[]>(`/production/shop-floor/operation/${String(operationId)}/count`, request);
  }

  /** Several steps at once: START, or FINISH after each row's good quantity (docs/cutting-jobs §8). */
  bulk(request: BulkStepRequest): Promise<BulkStepResult[]> {
    return this.http.post<BulkStepResult[]>("/production/shop-floor/operations/bulk", request);
  }
}

export class ProductionTransferClient extends ResourceClient<ProductionTransferDTO> {
  constructor(http: HttpClient) {
    super(http, "/production/production-transfer");
  }

  forLot(lotId: number): Promise<ProductionTransferDTO[]> {
    return this.find({ page: 0, size: 100, filters: { production_order_id: [String(lotId)] }, sort: ["number,desc"] }).then((page) => page.content);
  }

  /** Ships a truckload. 409 code CONFIRM when beyond what is finished: resend with {@link shipConfirmed}. */
  ship(transfer: ProductionTransferDTO): Promise<ProductionTransferDTO> {
    return this.create(transfer);
  }

  shipConfirmed(transfer: ProductionTransferDTO): Promise<ProductionTransferDTO> {
    return this.http.post<ProductionTransferDTO>(`${this.path}/confirmed`, transfer);
  }

  /** What each component can still ship, by component id. */
  ready(lotId: number): Promise<Record<string, number>> {
    return this.http.get<Record<string, number>>(`${this.path}/ready`, { query: { production_order_id: lotId } });
  }

  /** Lines left out are received in full. */
  receive(id: number, lines: Array<{ production_transfer_line_id: number; quantity_received: number; note?: string | null }>): Promise<ProductionTransferDTO> {
    return this.http.post<ProductionTransferDTO>(`${this.path}/${String(id)}/receive`, { lines });
  }

  cancel(id: number): Promise<ProductionTransferDTO> {
    return this.http.post<ProductionTransferDTO>(`${this.path}/${String(id)}/cancel`, {});
  }
}

/** Service lines made at the plant as a SERVICE lot. */
export class ServiceProductionClient {
  constructor(private readonly http: HttpClient) {}

  create(request: {
    lines: Array<{ service_order_line_id: number; quantity?: number | null }>;
    due_date?: string | null;
    note?: string | null;
    release?: boolean;
  }): Promise<ProductionOrderDTO> {
    return this.http.post<ProductionOrderDTO>("/servicedesk/production", request);
  }

  /** Open lines with something left to send and no lot making them. */
  queue(): Promise<ServiceOrderLineDTO[]> {
    return this.http.get<ServiceOrderLineDTO[]>("/servicedesk/production/queue");
  }

  lotsForOrder(serviceOrderId: number): Promise<ProductionOrderDTO[]> {
    return this.http.get<ProductionOrderDTO[]>(`/servicedesk/production/order/${String(serviceOrderId)}/lots`);
  }
}

export interface BulkStepRequest {
  kind: "START" | "FINISH";
  rows: Array<{ operation_id: number; quantity_good?: number | null }>;
  station_id: number | null;
  operator_badge: string | null;
}

/** One step of a bulk report: done, or why not. */
export interface BulkStepResult {
  operation_id: number;
  ok: boolean;
  message: string | null;
}
